import { EventContentArg, EventDropArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, { EventResizeDoneArg } from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import useAsyncGetScheduleCalendarEvents from '../../../hooks/useAsyncGetScheduleCalendarEvents';
import useWindowResize from '../../../hooks/useWindowSize';
import { CALENDAR_DEFAULT_USER_FILTER, CALENDAR_DEFAULT_VIEW } from '../../../sic-lib/defaults';
import { CalendarView } from '../../../sic-lib/enums/CalendarView';
import { enumToKey } from '../../../sic-lib/utils/functionalFilters';
import { setCalendarView } from '../../../store/calendar';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import FullCalendarEventContent from '../FullCalendarEventContent/FullCalendarEventContent';

const useStyles = makeStyles()(theme => ({
    calendarWrapper: {
        height: '100%',
    },
    root: {
        width: '200px',
        padding: theme.spacing(1),
    },
    inlineIconWrap: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        paddingBottom: theme.spacing(1),
    },
    typography: {
        //padding: theme.spacing(2),
    },
    inlineIcon: {
        marginRight: theme.spacing(1),
    },
}));

interface RangeApi {
    start: Date;
    end: Date;
    startStr: string;
    endStr: string;
}

export default function UserCalendar() {
    const { classes } = useStyles();
    const dispatch = useAppDispatch();

    //fitting the calendar into the drawer
    const calendarWrapperRef = React.useRef<HTMLDivElement | null>(null);

    const { windowHeight } = useWindowResize();

    React.useEffect(() => {
        if (calendarWrapperRef.current) {
            const { height } = calendarWrapperRef.current.getBoundingClientRect();

            setCalendarHeight(height);
        }
    }, [windowHeight]);

    const [calendarHeight, setCalendarHeight] = React.useState<number>(0);
    //console.log('calendarHeight', calendarHeight);

    const calendarRef = React.createRef<FullCalendar>();

    const calendarView = useAppSelector(
        state => state.calendar?.calendarView || CALENDAR_DEFAULT_VIEW,
    );
    const showWeekends = useAppSelector(state => state.calendar?.showWeekends || false);
    const userFilter = useAppSelector(
        state => state.calendar?.userFilter || CALENDAR_DEFAULT_USER_FILTER,
    );

    React.useEffect(() => {
        const calendarViewStr = enumToKey(CalendarView, calendarView);
        //console.log('enumKey', enumKey, calendarView)
        let calendarApi = calendarRef.current?.getApi();
        calendarApi?.changeView(calendarViewStr as string);
    }, [calendarView]);

    const calendarViewStr = enumToKey(CalendarView, calendarView);

    const { requestStatus, getContactCalendarEventsResult, asyncGetCalendarEventsByRange } =
        useAsyncGetScheduleCalendarEvents();

    const [range, setRange] = React.useState<RangeApi>();

    //request the events
    const requestEvents = async (rangeInfo: RangeApi) => {
        /*console.log('requestEvents');*/
        setRange(rangeInfo);
        asyncGetCalendarEventsByRange(rangeInfo.start, rangeInfo.end, userFilter.userName);
    };

    //when the username is changed, tell the calendar to refresh
    React.useEffect(() => {
        if (range) {
            asyncGetCalendarEventsByRange(range.start, range.end, userFilter.userName);
        }
        /*console.log('calendarState.userFilter.userName CHANGED', calendarState.userFilter.userName)*/
    }, [userFilter.userName, showWeekends, calendarView]);

    const onNewSlotSelect = (data: any) => {
        /*console.log('onNewSlotSelect', calendarState.calendarView)*/
        /*dispatch(calendarActions.openDrawerNewCalendarEvent(data.start, data.end, "One-off Event"));*/
    };

    const onDateClicked = (data: any) => {
        /*console.log('onDateClicked', calendarState.calendarView);*/

        switch (calendarView) {
            case CalendarView.dayGridMonth:
                let calendarApi = calendarRef.current?.getApi();
                dispatch(setCalendarView(CalendarView.timeGridWeek));
                calendarApi?.gotoDate(data.dateStr);
                break;
        }

        /*dispatch(calendarActions.openDrawerNewCalendarEvent(data.start, data.end, "One-off Event"));*/
    };

    const onEventDroppedOrResized = (eventInfo: EventDropArg | EventResizeDoneArg) => {
        const calendarEvent = getContactCalendarEventsResult.find(
            x => x.eventId == +eventInfo.event.id,
        );

        if (calendarEvent && eventInfo.event.start && eventInfo.event.end) {
            // TODO
            // dispatch(
            //     calendarActions.updateCalendarEvent(
            //         calendarEvent.eventId,
            //         mapCalendarEventRequest({
            //             ...calendarEvent,
            //             startDate: DateTime.fromJSDate(eventInfo.event.start),
            //             endDate: DateTime.fromJSDate(eventInfo.event.end),
            //         }),
            //     ),
            // );
        }
    };

    return (
        <div className={classes.calendarWrapper} ref={calendarWrapperRef}>
            <FullCalendar
                ref={calendarRef}
                themeSystem="materia"
                plugins={[dayGridPlugin, listPlugin, timeGridPlugin, interactionPlugin]}
                initialView={calendarViewStr}
                height={calendarHeight}
                firstDay={0}
                allDaySlot={true}
                editable={true}
                eventDrop={onEventDroppedOrResized}
                eventResize={onEventDroppedOrResized}
                /*selectable={true}
                selectMirror={true}*/
                weekends={showWeekends}
                select={onNewSlotSelect}
                dateClick={onDateClicked}
                events={getContactCalendarEventsResult}
                datesSet={requestEvents}
                eventContent={(calendarEvent: EventContentArg) => (
                    <FullCalendarEventContent
                        fcCalendarEvent={calendarEvent}
                        allEvents={getContactCalendarEventsResult}
                    />
                )}
            />
        </div>
    );
}
